import { ModelEntityType } from '@upright/common/model/input/types';
import { type ModelEntitySearchResponseItem, type SearchEntityType } from '@upright/common/model/search/types';
import { type StartMenuCategory } from '@upright/ui/common/upright/internal/start-menu/menu-data';

/**
 * The pure half of the unified shell's search panel (PLT-7168, PLT-7169): which types a filter chip
 * searches, how the backend's ranked hits are grouped into the panel's sections, which internal pages
 * match, and where the "Ask Upright AI" row goes. Kept apart from the components so the rules can be
 * tested without rendering.
 */

export type SearchFilterId =
  | 'all'
  | 'companies'
  | 'funds'
  | 'portfolios'
  | 'products'
  | 'mixins'
  | 'phenomena'
  | 'legal-entities';

export interface SearchFilter {
  id: SearchFilterId;
  label: string;
  /** What the chip narrows the search to. `null` is Everything: the audience's full scope. */
  types: readonly SearchEntityType[] | null;
  /** One of the team's additions, shown after a divider and only with team details on. */
  teamOnly?: boolean;
}

/**
 * The type filter, in chip order: the legacy dark-header search's "Search for" list, with "Funds or
 * indices" shortened to "Funds". Single-select, Everything by default. Products is offered only to
 * users who can search products, as the field's hint already decides. With team details on, the
 * team-only types follow (PLT-7169).
 */
export function searchFilters({
  canSearchProducts,
  teamDetails,
}: {
  canSearchProducts: boolean;
  teamDetails: boolean;
}): SearchFilter[] {
  const filters: SearchFilter[] = [
    { id: 'all', label: 'Everything', types: null },
    { id: 'companies', label: 'Companies', types: [ModelEntityType.COMPANY] },
    { id: 'funds', label: 'Funds', types: [ModelEntityType.GROUP] },
    { id: 'portfolios', label: 'Portfolios', types: [ModelEntityType.PORTFOLIO] },
  ];
  if (canSearchProducts || teamDetails) {
    filters.push({ id: 'products', label: 'Products', types: [ModelEntityType.PRODUCT] });
  }
  if (teamDetails) {
    filters.push(
      { id: 'mixins', label: 'Mixins', types: [ModelEntityType.MIXIN], teamOnly: true },
      { id: 'phenomena', label: 'Phenomena', types: [ModelEntityType.PHENOMENON], teamOnly: true },
      { id: 'legal-entities', label: 'Legal entities', types: ['legalEntity'], teamOnly: true },
    );
  }
  return filters;
}

export interface InternalPageHit {
  url: string;
  label: string;
  /** Where the page sits in the internal-tools menu, as "Category" or "Category · Submenu". */
  location: string;
  external: boolean;
}

/**
 * The internal pages whose name and menu location together contain every word of the query, in
 * any order ("eevo funnel" finds Funnel under Eevo · Marketing), in menu order; every page
 * when nothing is typed, so the team's shortcut is also the fastest way into the internal tools.
 * They come from the internal-tools menu (`menu-data.ts`), which is the one index of them. Rows
 * that run an action instead of opening a page are left out, and a page listed twice appears once.
 */
export function internalPageHits(categories: readonly StartMenuCategory[], query: string): InternalPageHit[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const pages = new Map<string, InternalPageHit>();
  const add = (links: StartMenuCategory['links'], location: string) => {
    for (const link of links) {
      if (link.url === undefined || pages.has(link.url)) {
        continue;
      }
      pages.set(link.url, {
        url: link.url,
        label: link.label,
        location,
        external: link.external ?? /^https?:\/\//.test(link.url),
      });
    }
  };
  for (const category of categories) {
    add(category.links, category.label);
    for (const subcategory of category.subcategories ?? []) {
      add(subcategory.links, `${category.label} · ${subcategory.label}`);
    }
  }
  const all = [...pages.values()];
  if (words.length === 0) {
    return all;
  }
  return all.filter(page => {
    const text = `${page.label} ${page.location}`.toLowerCase();
    return words.every(word => text.includes(word));
  });
}

/** The panel's result sections. Every entity type belongs to exactly one. */
const RESULT_GROUPS: ReadonlyArray<{ id: string; label: string; types: readonly ModelEntityType[] }> = [
  { id: 'companies', label: 'Companies', types: [ModelEntityType.COMPANY] },
  { id: 'funds', label: 'Funds and portfolios', types: [ModelEntityType.GROUP, ModelEntityType.PORTFOLIO] },
  { id: 'products', label: 'Products', types: [ModelEntityType.PRODUCT, ModelEntityType.MIXIN] },
  { id: 'phenomena', label: 'Phenomena', types: [ModelEntityType.PHENOMENON] },
];

export interface SearchResultGroup {
  id: string;
  label: string;
  hits: ModelEntitySearchResponseItem[];
}

/**
 * The hits in sections, keeping the backend's ranking twice over: a section comes where its
 * best-ranked hit came, and its hits stay in rank order. A fixed section order would put the best
 * match under a weaker one whenever it was, say, a fund, and Enter opens the first row.
 */
export function groupSearchHits(hits: readonly ModelEntitySearchResponseItem[]): SearchResultGroup[] {
  const groups = new Map<string, SearchResultGroup>();
  for (const hit of hits) {
    const definition = RESULT_GROUPS.find(group => group.types.includes(hit.type));
    if (definition === undefined) {
      continue;
    }
    const group = groups.get(definition.id) ?? { id: definition.id, label: definition.label, hits: [] };
    group.hits.push(hit);
    groups.set(definition.id, group);
  }
  return [...groups.values()];
}

/**
 * A rough read of "this is a question, not a name": a question mark, or a question or command word
 * first. Not the number of words: fund names run to five or six ("iShares Core MSCI World UCITS
 * ETF"), and with the AI row listed first the row `Autocomplete` highlights as the reader types is
 * the AI row, so Enter on a pasted name started a chat. The guess only decides which comes first;
 * the AI row and the results are both on screen either way.
 */
export function isQuestionLike(query: string): boolean {
  const text = query.trim().toLowerCase();
  if (text === '') {
    return false;
  }
  const firstWord = text.split(/\s+/)[0];
  const questionWords = ['which', 'what', 'how', 'why', 'who', 'where', 'when', 'compare', 'show', 'find', 'list'];
  return text.endsWith('?') || questionWords.includes(firstWord);
}

/**
 * What Enter opens when no row is highlighted: the first result whenever there is one, and the AI
 * row only when nothing matches. The AI row can be listed first for question-like text, but Enter
 * does not follow it there: `isQuestionLike` is a guess, and a reader who typed a name and pressed
 * Enter expects to arrive at it, not to start a chat that sends the name to a model. With nothing
 * typed, Enter opens nothing, so a stray keypress on an empty panel does not navigate away.
 */
export function enterFallback({
  hasQuery,
  hasResult,
  canAsk,
}: {
  hasQuery: boolean;
  hasResult: boolean;
  canAsk: boolean;
}): 'result' | 'ask' | null {
  if (!hasQuery) {
    return null;
  }
  if (hasResult) {
    return 'result';
  }
  return canAsk ? 'ask' : null;
}

/**
 * Where the "Ask Upright AI" row goes. Absent while nothing searchable is typed, since there is
 * nothing to hand to the chat. First when nothing matches or the query reads like a question;
 * otherwise last, a step past the results, because a name was most likely what was wanted.
 */
export function askRowPosition({
  query,
  isSearchTriggered,
  hasResults,
}: {
  query: string;
  isSearchTriggered: boolean;
  hasResults: boolean;
}): 'first' | 'last' | null {
  if (!isSearchTriggered) {
    return null;
  }
  return !hasResults || isQuestionLike(query) ? 'first' : 'last';
}
